import React, { useEffect, useMemo, useRef, useState } from "react";
import confetti from "canvas-confetti";
import Container from "../../../../CommonElements/Container";
import SectionHeading from "../../../../CommonElements/SectionHeading";
import { PRIZE_TROPHY_BY_PLACE } from "../../../../CommonElements/PrizeTrophy";
import { PRIZES } from "../../../../Data/prizes";
import { useLanguage } from "../../../../context/LanguageContext";
import useReveal from "../../../../hooks/useReveal";
import { fetchLeaderboard } from "../../../../utils/api";
import { isStaticDataMode } from "../../../../utils/staticMode";

const PLACE_I18N = [
  "prizes.firstPlace",
  "prizes.secondPlace",
  "prizes.thirdPlace",
  "prizes.fourthPlace",
  "prizes.fifthPlace",
  "prizes.sixthPlace",
  "prizes.seventhPlace",
];

/** Figma winners cards use short ordinals (1st, 2nd…), not "1st Place". */
const PLACE_SHORT = ["1st", "2nd", "3rd", "4th", "5th", "6th", "7th"];

const CONFETTI_COLORS = [
  "#f5a623",
  "#1e4fd6",
  "#e07020",
  "#3f7fd8",
  "#6b5ce7",
  "#12b76a",
  "#f04438",
  "#ffe08a",
  "#ffffff",
];

const formatPerformance = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
};

const mapWinnerRow = (row, index) => {
  const place = Number(row.place) || index + 1;
  const prize = PRIZES[place - 1] || {};
  return {
    place,
    trader: String(row.trader || "").trim(),
    email: String(row.email || "").trim(),
    verified: row.verified !== false,
    nation: row.nation || "",
    amount: row.prizeAmount || prize.amount || "",
    performancePct: row.performancePct ?? row.performance,
    accent: prize.accent || "blue",
  };
};

/** Local design-only. Never active on UAT/prod hostnames. */
const isWinnersPreviewMode = () => {
  if (String(process.env.REACT_APP_PREVIEW_WINNERS || "").trim() !== "1") {
    return false;
  }
  if (typeof window === "undefined") return false;
  const host = String(window.location.hostname || "").toLowerCase();
  return host === "localhost" || host === "127.0.0.1";
};

const PREVIEW_WINNERS = [
  { place: 1, trader: "Metehan_Ozturk", performancePct: 179.57 },
  { place: 2, trader: "CrazyGirl46",performancePct: 101.69 },
  { place: 3, trader: "ABDULLAH PARLAK", performancePct: 90.36 },
  { place: 4, trader: "AhmetOzkan", performancePct: 87.15 },
  { place: 5, trader: "Hasan Randa", performancePct: 64.85 },
  { place: 6, trader: "AnilErsoy", performancePct: 40.67 },
  { place: 7, trader: "Ozlem Nakisli", performancePct: 11.58 },
].map((row, index) =>
  mapWinnerRow(
    {
      ...row,
      prizeAmount: PRIZES[index]?.amount,
      verified: true,
    },
    index,
  ),
);

/** Public board: only frozen CompetitionWinners (never live board preview). */
const resolveFrozenWinners = (payload = {}) => {
  if (!payload.frozen) return [];
  const stored = Array.isArray(payload.winners) ? payload.winners : [];
  return stored
    .map(mapWinnerRow)
    .filter((row) => row.trader && row.place >= 1 && row.place <= 7)
    .sort((a, b) => a.place - b.place);
};

/** Side cannons + center fountain — lasting celebration burst. */
const fireWinnersConfetti = () => {
  if (typeof window === "undefined") return;
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) return;

  const base = {
    colors: CONFETTI_COLORS,
    disableForReducedMotion: true,
    zIndex: 60,
    gravity: 0.75,
    drift: 0.4,
    ticks: 380,
    scalar: 1.05,
  };

  const cannon = (originX, angle) => {
    confetti({
      ...base,
      particleCount: 110,
      angle,
      spread: 68,
      startVelocity: 58,
      origin: { x: originX, y: 0.72 },
    });
  };

  cannon(0, 62);
  cannon(1, 118);

  // Center burst
  confetti({
    ...base,
    particleCount: 140,
    spread: 360,
    startVelocity: 42,
    origin: { x: 0.5, y: 0.42 },
    ticks: 420,
  });

  // Keep confetti raining briefly so it does not vanish instantly
  const endAt = Date.now() + 2200;
  const rain = () => {
    confetti({
      ...base,
      particleCount: 22,
      spread: 110,
      startVelocity: 32,
      origin: {
        x: 0.15 + Math.random() * 0.7,
        y: 0.2 + Math.random() * 0.15,
      },
      ticks: 360,
      gravity: 0.7,
    });
    if (Date.now() < endAt) {
      window.setTimeout(rain, 280);
    }
  };
  window.setTimeout(rain, 220);

  // Finale pop
  window.setTimeout(() => {
    confetti({
      ...base,
      particleCount: 90,
      spread: 100,
      startVelocity: 48,
      origin: { x: 0.5, y: 0.5 },
      ticks: 400,
    });
  }, 900);
};

const WinnerCard = ({ winner }) => {
  const { t } = useLanguage();
  if (!winner?.trader) return null;

  const placeIndex = winner.place - 1;
  const placeLabel =
    PLACE_SHORT[placeIndex] || t(PLACE_I18N[placeIndex] || "prizes.firstPlace");
  const showTrophy = winner.place <= 3;
  const trophySrc = PRIZE_TROPHY_BY_PLACE[placeIndex];

  return (
    <article
      className={`winners-stage__card winners-stage__card--${winner.accent}`}
    >
      <div className="winners-stage__card-top">
        <div className="winners-stage__card-main">
          <span className="winners-stage__place">{placeLabel}</span>

          <div className="winners-stage__who">
            <div className="winners-stage__who-row">
              <p className="winners-stage__name" title={winner.trader}>
                {winner.trader}
              </p>
              {winner.verified ? (
                <span className="winners-stage__verified">
                  <svg
                    className="winners-stage__verified-icon"
                    viewBox="0 0 16 16"
                    aria-hidden="true"
                  >
                    <circle cx="8" cy="8" r="8" fill="#12B76A" />
                    <path
                      d="M4.6 8.2l2.1 2.1 4.7-4.7"
                      fill="none"
                      stroke="#fff"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {t("leaderboard.verified")}
                </span>
              ) : null}
            </div>
            {/* {winner.email ? (
              <p className="winners-stage__email" title={winner.email}>
                {winner.email}
              </p>
            ) : null} */}
          </div>
        </div>

        {showTrophy && trophySrc ? (
          <img
            className={`winners-stage__trophy winners-stage__trophy--p${winner.place}`}
            src={trophySrc}
            alt=""
            width={120}
            height={120}
            decoding="async"
            loading="lazy"
          />
        ) : null}
      </div>

      <div className="winners-stage__metrics">
        <div className="winners-stage__amount-block">
          <span className="winners-stage__amount-label">
            {t("leaderboard.amount")}
          </span>
          <p className="winners-stage__amount">{winner.amount}</p>
        </div>
        <p className="winners-stage__performance">
          {formatPerformance(winner.performancePct)}
        </p>
      </div>
    </article>
  );
};

const WinnersStage = () => {
  const { t } = useLanguage();
  const [revealRef] = useReveal(0.15);
  const [winners, setWinners] = useState([]);
  const [ready, setReady] = useState(false);
  const confettiFiredRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const preview = isWinnersPreviewMode();

    const load = async () => {
      try {
        // Design-only local preview — never ship with REACT_APP_PREVIEW_WINNERS=1.
        if (preview) {
          if (!cancelled) {
            setWinners(PREVIEW_WINNERS);
            setReady(true);
          }
          return;
        }

        if (isStaticDataMode()) {
          if (!cancelled) setReady(true);
          return;
        }

        const { ok, payload } = await fetchLeaderboard();
        if (cancelled) return;

        const result = payload?.result || {};
        // Hidden until competition freeze; then show persisted winners only.
        const resolved = ok ? resolveFrozenWinners(result) : [];
        setWinners(resolved);
      } catch {
        if (!cancelled) setWinners([]);
      } finally {
        if (!cancelled) setReady(true);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ready || !winners.length || confettiFiredRef.current) return undefined;

    const node = revealRef?.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      confettiFiredRef.current = true;
      const t = window.setTimeout(fireWinnersConfetti, 280);
      return () => window.clearTimeout(t);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || confettiFiredRef.current) return;
        confettiFiredRef.current = true;
        fireWinnersConfetti();
        observer.disconnect();
      },
      { threshold: 0.28, rootMargin: "0px 0px -8% 0px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [ready, winners.length, revealRef]);

  const topRow = useMemo(
    () => winners.filter((w) => w.place >= 1 && w.place <= 3),
    [winners],
  );
  const bottomRow = useMemo(
    () => winners.filter((w) => w.place >= 4 && w.place <= 7),
    [winners],
  );

  if (!ready || !winners.length) return null;

  return (
    <section
      className="section winners-stage is-in winners-stage--frozen"
      id="winners"
      ref={revealRef}
      aria-label={t("leaderboard.winnersTitle")}
    >
      <Container>
        <SectionHeading title={t("leaderboard.winnersTitle")} />

        <div className="winners-stage__grid">
          <div className="winners-stage__row winners-stage__row--top">
            {topRow.map((winner) => (
              <WinnerCard key={`place-${winner.place}`} winner={winner} />
            ))}
          </div>
          <div className="winners-stage__row winners-stage__row--bottom">
            {bottomRow.map((winner) => (
              <WinnerCard key={`place-${winner.place}`} winner={winner} />
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
};

export default WinnersStage;
